import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, track } from "../lib/api";
import { Check, X, Clock, Mail, LogIn } from "lucide-react";

const courseHome = (courseId) =>
  courseId === "becoming-an-estate-manager" ? "/becoming"
    : courseId === "residence-service-record" ? "/tool/residence-service-record"
      : courseId ? `/learn/${courseId}` : "/account";

const btn = "bg-ink text-bg px-10 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors";
const Icon = ({ children }) => <div className="mx-auto w-14 h-14 border border-ink flex items-center justify-center">{children}</div>;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  // checking | enrolled | activation | existing | duplicate | processing | error
  const [status, setStatus] = useState("checking");
  const [courseId, setCourseId] = useState(null);
  const [emailHint, setEmailHint] = useState("");

  useEffect(() => { document.title = "Payment · Il Salotto Digitale"; }, []);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      let paid = false;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setCourseId(data.course_id);
        setEmailHint(data.email_hint || "");
        if (data.payment_status === "paid") {
          paid = true;
          if (data.course_id && !sessionStorage.getItem(`mb_pc_${sessionId}`)) {
            sessionStorage.setItem(`mb_pc_${sessionId}`, "1");
            track("purchase_confirmed", data.course_id);
          }
          const map = { signed_in_owner: "enrolled", activation_sent: "activation", existing_account: "existing", duplicate: "duplicate" };
          if (map[data.access]) { setStatus(map[data.access]); return; }
        } else if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch (e) { /* keep polling */ }
      if (tries++ < 10) { timer = setTimeout(poll, 1800); return; }
      setStatus(paid ? "processing" : "error");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen pt-20 flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center border border-line p-10 sm:p-12 bg-surface">
        {status === "checking" && (
          <>
            <p className="text-[11px] uppercase tracking-ultra text-accent animate-pulse">Confirming payment…</p>
            <h1 className="mt-6 font-serif text-3xl text-ink">One moment</h1>
            <p className="mt-3 text-sm text-ink-soft">We're confirming your payment and preparing your access.</p>
          </>
        )}
        {status === "enrolled" && (
          <>
            <Icon><Check size={26} /></Icon>
            <h1 data-testid="payment-enrolled-heading" className="mt-6 font-serif text-4xl text-ink">You're enrolled</h1>
            <p className="mt-3 text-sm text-ink-soft">Payment confirmed and your course is now in your library.</p>
            <button data-testid="go-to-course" onClick={() => nav(courseHome(courseId))} className={`mt-8 ${btn}`}>Start learning</button>
          </>
        )}
        {status === "activation" && <ActivationState emailHint={emailHint} />}
        {status === "existing" && (
          <>
            <Icon><LogIn size={24} /></Icon>
            <h1 data-testid="payment-existing-heading" className="mt-6 font-serif text-3xl text-ink">Payment confirmed</h1>
            <p className="mt-3 text-sm text-ink-soft">
              Your purchase has been added to the existing account for <span className="text-ink">{emailHint}</span>. Sign in to open it.
            </p>
            <button data-testid="existing-sign-in" onClick={() => nav("/login", { state: { from: courseHome(courseId) } })} className={`mt-8 ${btn}`}>Sign in to access</button>
            <p className="mt-5 text-xs text-ink-soft">Forgotten your password? Use <Link to="/forgot-password" className="underline">Forgot password</Link> on the sign-in page.</p>
          </>
        )}
        {status === "duplicate" && (
          <>
            <Icon><Check size={26} /></Icon>
            <h1 data-testid="payment-duplicate-heading" className="mt-6 font-serif text-3xl text-ink">You already own this</h1>
            <p className="mt-3 text-sm text-ink-soft">
              The account for <span className="text-ink">{emailHint}</span> already has access to this product, so no second copy was added. We've been notified and will review this payment for a refund — no action needed on your side.
            </p>
            <button data-testid="duplicate-sign-in" onClick={() => nav("/login", { state: { from: courseHome(courseId) } })} className={`mt-8 ${btn}`}>Sign in to your library</button>
          </>
        )}
        {status === "processing" && (
          <>
            <Icon><Clock size={24} /></Icon>
            <h1 data-testid="payment-processing-heading" className="mt-6 font-serif text-3xl text-ink">Payment received</h1>
            <p className="mt-3 text-sm text-ink-soft">Your payment was confirmed and we're finalizing your access. This can take a moment.</p>
            <div className="mt-8 flex flex-col gap-3">
              <button data-testid="processing-refresh" onClick={() => window.location.reload()} className={btn}>Refresh</button>
              <Link to="/contact" className="text-[11px] uppercase tracking-[0.2em] text-ink-soft hover:text-ink transition-colors">Contact us</Link>
            </div>
          </>
        )}
        {status === "error" && (
          <>
            <Icon><X size={26} /></Icon>
            <h1 data-testid="payment-error-heading" className="mt-6 font-serif text-3xl text-ink">Couldn't confirm</h1>
            <p className="mt-3 text-sm text-ink-soft">If you completed payment, it may take a moment. Check your email or contact us.</p>
            <Link to="/contact" className={`mt-8 inline-block ${btn}`}>Contact us</Link>
          </>
        )}
      </div>
    </div>
  );
}

function ActivationState({ emailHint }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const resend = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/resend-activation", { email }); setSent(true); toast.success("If an account is awaiting activation, a new link is on its way."); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Icon><Mail size={24} /></Icon>
      <h1 data-testid="payment-activation-heading" className="mt-6 font-serif text-3xl text-ink">Check your email</h1>
      <p className="mt-3 text-sm text-ink-soft">
        Payment confirmed. We've sent a secure activation link to <span className="text-ink" data-testid="activation-email-hint">{emailHint}</span>.
        Open it to choose a password and enter your library. The link is valid for 48 hours and can only be used once.
      </p>
      <p className="mt-6 text-xs text-ink-soft">Didn't receive it? Check spam, or enter your email to send it again.</p>
      <form onSubmit={resend} className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="resend-activation-form">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used at checkout" data-testid="resend-activation-email"
          className="flex-1 border border-line bg-transparent px-4 py-3 text-sm text-ink focus:outline-none focus:border-ink" />
        <button disabled={busy || sent} data-testid="resend-activation-submit" className={`${btn} px-6 disabled:opacity-60`}>{sent ? "Sent" : "Resend"}</button>
      </form>
    </>
  );
}
